import { css as cssClass } from "@emotion/css";
import { Dialog, DialogFooter } from "@fluentui/react";
import { formatISO } from "date-fns";
import { Formik } from "formik";
import moment from "moment";

import { DialogType, mergeStyleSets } from "@fluentui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { FormSpinner } from "../../../components/FormSpinner";
import PinLocationsList from "../../../components/PinLocationsList";
import DatePickerField from "../../../components/controls/DatePickerField";
import DialogButton from "../../../components/controls/DialogButton";
import { Field } from "../../../components/form/Field";
import { routes } from "../../../config/routes";
import { useSelector } from "../../../store/hooks";
import { addPropertyInfoActionsRoutine } from "../../../store/valueMonitoring/valueMonitoringPage";

type Props = {
  close: () => void;
  values: any;
  coveragePeriodFromDate: any;
  coveragePeriodToDate: any;
};

const SendInvitationFormDialog = ({
  close,
  values,
  coveragePeriodFromDate,
  coveragePeriodToDate,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [didTrySubmit, setDidTrySubmit] = useState(false);
  const isLoading =
    useSelector((s) => s.valueMonitoring.dataLoadStatus) === "loading";
  const clientId = useSelector((s) => s.valueMonitoring.formObject?.clientId);

  const navigate = useNavigate();
  const selectedLocation = useSelector(
    (s) => s.valueMonitoring.selectedLocation
  );
  const selection = [selectedLocation];

  const onSubmit = async (data) => {
    const formObject = values.map((value) => {
      return {
        // buildingValues: stringToNumber(value.buildingValues),
        // equipmentValues: stringToNumber(value.equipmentValues),
        // stockValues: stringToNumber(value.stockValues),
        // totalPd: stringToNumber(value.totalPd),
        // businessInteruptionValues12Months: stringToNumber(
        //   value.businessInteruptionValues12Months
        // ),
        // totalPdBi: stringToNumber(value.totalPdBi),
        // indemnityPeriodCode: value.indemnityPeriodCode?.value,
        // businessInteruptionValuesBasedOnIndemnityPeriod: stringToNumber(
        //   value.businessInteruptionValuesBasedOnIndemnityPeriod
        // ),
        // totalPdBiBasedOnIndemnityPeriod: stringToNumber(
        //   value.totalPdBiBasedOnIndemnityPeriod
        // ),
        desiredLocationOwningStatusCode: value.location.locationOwningStatus,

        desiredLocationOwningStatusDate: value.location.locationOwningStatusDate
          ? formatISO(
              moment(value.location.locationOwningStatusDate).toDate(),
              {
                representation: "date",
              }
            )
          : "",
        desiredLocationStatusCode: value.location.locationStatus,
        desiredLocationStatusDate: value.location.locationStatusDate
          ? formatISO(moment(value.location.locationStatusDate).toDate(), {
              representation: "date",
            })
          : "",

        grECoComment: data.comment,
        coveragePeriodFromDate: formatISO(data.coveragePeriodFromDate, {
          representation: "date",
        }),
        coveragePeriodToDate: formatISO(data.coveragePeriodToDate, {
          representation: "date",
        }),
        currencyCode: value.location.currencyCode,
        clientId: value.clientId,
        locationId: value.locationId,
      };
    });
    await dispatch(
      addPropertyInfoActionsRoutine.trigger({
        clientId,
        formObject,
        onSuccess: (clientId, locationId, propertyInformationId) => {
          navigate(
            routes.valueMonitoringForLocation.getPath(
              clientId,
              locationId,
              propertyInformationId
            )
          );
          // dispatch(loadLocationsForClientRoutine.trigger({ clientId }));
        },
      })
    );
    close();
  };

  const title = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
      }}
    >
      {t("rvm.sendInvitation.label")}
    </div>
  );
  const validationSchema = useMemo(() => {
    const schemaObj = {
      coveragePeriodFromDate: yup.date().required(),
      coveragePeriodToDate: yup.date().required(),
      comment: yup.string().max(512).nullable(),
    };
    return yup.object().shape(schemaObj);
  }, []);

  const formStyles = mergeStyleSets({
    containerOne: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
      gridColumnGap: "20px",
      gridRowGap: "20px",
      padding: "20px",
      paddingBottom: "30px",
      "& .grid-item0": {
        gridColumn: "1 / span 1",
      },
      "& .grid-item1": {
        gridColumn: "2 / span 1",
        height: "280px",
        overflowY: "scroll",
      },

      // "& .grid-item12": {
      //   gridColumn: "1 / span 3",
      // },
    },

    containerGrid50: {
      display: "flex",
      gap: "20px",
      justifyContent: "flex-start",
      width: "66%",
    },
    containerGrid50Item: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    containerGrid: {
      display: "flex",
      gap: "20px",
      justifyContent: "flex-start",
    },
    containerGridGrow: {
      flexGrow: "1",
    },
    boldSpan: {
      fontWeight: "600",
    },
    infoLabel: {
      margin: "50px 0px",
      float: "right",
      color: "red",
    },
  });

  const allLocationsHaveContactPersons = selection.reduce(function (
    accumulator,
    currentValue
  ) {
    return (
      accumulator &&
      (currentValue.location.contactPersons.length === 0 ? false : true)
    );
  },
  true);

  return (
    <Dialog
      hidden={false}
      onDismiss={close}
      dialogContentProps={{
        type: DialogType.normal,
        title,
        className: cssClass`
        .ms-Dialog-title {
          margin: 20px;
          padding-bottom: 10px;
        }
        .ms-Dialog-inner {
          margin: 20px;
          padding-bottom: 14px;
        }
        .ms-Button {
          padding: 10px 35px;
          //background: rgb(0, 184, 238);
        }
      `,
        showCloseButton: true,
      }}
      minWidth={800}
      modalProps={{
        isBlocking: true, //phraseActivated ? false :
      }}
    >
      <Formik
        initialValues={{
          comment: "",
          coveragePeriodFromDate: null,
          coveragePeriodToDate: null,
        }}
        onSubmit={onSubmit}
        validateOnChange={didTrySubmit}
        validateOnBlur={didTrySubmit}
        validationSchema={validationSchema}
      >
        {({ submitForm, errors, setFieldValue }) => {
          return (
            <>
              <div className={formStyles.containerGrid50}>
                <div className={formStyles.containerGrid50Item}>
                  <span className={formStyles.boldSpan}>From&nbsp;*</span>
                  <DatePickerField
                    errors={errors}
                    setFieldValue={setFieldValue}
                    defaultValue={coveragePeriodFromDate}
                    name={"coveragePeriodFromDate"}
                    disabled={false}
                    label={""}
                    placeholder={t("rvm.selectDate")}
                    isMarked={false}
                  />
                </div>
                <div className={formStyles.containerGrid50Item}>
                  <span className={formStyles.boldSpan}>To&nbsp;*</span>
                  <DatePickerField
                    errors={errors}
                    setFieldValue={setFieldValue}
                    defaultValue={coveragePeriodToDate}
                    name={"coveragePeriodToDate"}
                    disabled={false}
                    label={""}
                    placeholder={t("rvm.selectDate")}
                    isMarked={false}
                  />
                </div>
              </div>
              <div className={formStyles.containerGrid}>
                <div className={formStyles.containerGridGrow}>
                  <PinLocationsList selection={selection} />
                </div>
              </div>
              {allLocationsHaveContactPersons ? (
                <div className={formStyles.containerGrid}>
                  <div className={formStyles.containerGridGrow}>
                    <Field
                      name="comment"
                      type="text"
                      label={t("rvm.clientComment.label")}
                      isMultiline
                      rows={1}
                      isRequired={false}
                    />
                  </div>
                </div>
              ) : null}
              {allLocationsHaveContactPersons ? (
                <DialogFooter>
                  <DialogButton
                    text={t("rvm.send.label")}
                    disabled={isLoading}
                    operationFn={() => {
                      setDidTrySubmit(true);
                      submitForm();
                    }}
                  />
                </DialogFooter>
              ) : (
                <div className={formStyles.infoLabel}>
                  {t("rvm.locationMustHaveContactPersion.label")}
                </div>
              )}
              {isLoading && <FormSpinner />}
            </>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default SendInvitationFormDialog;
